import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        warning:
          'border-transparent bg-warning text-warning-foreground [a&]:hover:bg-warning/90 focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40',
        success:
          'border-transparent bg-success text-white [a&]:hover:bg-success/90 focus-visible:ring-success/20',
        info: 'border-transparent bg-info text-info-foreground [a&]:hover:bg-info/90 focus-visible:ring-info/20',
        accent:
          'border-transparent bg-violet-500 text-white [a&]:hover:bg-violet-500/90 focus-visible:ring-violet-500/20 dark:bg-violet-600',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
      },
      highContrast: {
        true: '',
      },
    },
    compoundVariants: [
      { variant: 'default', highContrast: true, class: 'bg-primary/90 text-primary-foreground' },
      {
        variant: 'secondary',
        highContrast: true,
        // Dark mode gets a mid-gray fill instead of the light-mode inversion —
        // inverting there produces a glaring near-white pill on the dark table.
        class:
          'bg-secondary-foreground text-secondary dark:bg-muted-foreground dark:text-background',
      },
      {
        variant: 'destructive',
        highContrast: true,
        class: 'bg-destructive text-white dark:bg-destructive',
      },
      {
        variant: 'warning',
        highContrast: true,
        class: 'bg-warning text-warning-foreground',
      },
      {
        variant: 'success',
        highContrast: true,
        class: 'bg-success text-white border-transparent',
      },
      { variant: 'info', highContrast: true, class: 'bg-info text-info-foreground' },
      {
        variant: 'accent',
        highContrast: true,
        class: 'bg-violet-600 text-white dark:bg-violet-500',
      },
      { variant: 'outline', highContrast: true, class: 'border-foreground text-foreground' },
    ],
    defaultVariants: {
      variant: 'default',
      highContrast: false,
    },
  }
);

function Badge({
  className,
  variant,
  highContrast,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, highContrast }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
